import axios, {AxiosHeaders, AxiosRequestConfig, AxiosResponse} from "axios";

export type Response<Data> = Promise<AxiosResponse<Data>>

export type WorkspaceId = number;
export type JourneyId = string;
export type PersonId = string;
export type OrganizationId = string;
export type PromotionId = string;

export type Url = string;
export type Email = string;
export type DateString = string;
export type OnboardingTag = string; // Enum but only used on the my-omind.
export type Version = string;
export type VrPin = string;

export type Lang = 'fr' | 'en' | 'es';
export type WorkspacePersonRoleCategory = 'coachee' | 'staff';
export type PersonRole = 'admin' | 'coach' | 'extern' | 'coachee';
export type MeetingName = 'feta_action_plan_meeting';
export type JourneyName =
  | 'ascent_online'
  | 'ascent_vr'
  | 'bilan_vr'
  | 'bilan_vr_flash'
  | 'bilan_vr_quest'
  | 'bootcamp_online'
  | 'feta'
  | 'ignition_online'
  | 'ignition_vr'
  | 'leadership'
  | 'resilience'
  | 'tingari'
  | 'ai_research'
  | 'feta_ascent';
export type JourneyStepName =
  | 'threshold'
  | 'impact_threshold'
  | 'feta_action_plan'
  | 'elearning_program'
  | 'feta_behaviors_awareness'
  | 'igt'
  | 'skills_synthesis'
  | 'my_omind_invitation'; // TODO;
export type JourneyStepId =
  | 'feta_launch_threshold'
  | 'feta_launch_onboarding'
  | 'feta_ascent_threshold'
  | 'feta_action_plan'
  | 'elearning_program'
  | 'feta_impact_threshold'
  | 'synthesis'
  | 'experience_synthesis'
  | 'experience_recommendation'
  // Bootcamp online customization
  | 'experience_emo_subj_sensitivity'
  | 'experience_emoquotation'
  | 'experience_emo_subj_regulation'
  | 'experience_oddball'
  | 'experience_scs-sf'
  | 'experience_oth_ohls'
  | 'experience_cog_subj_focus'
  | 'experience_focus'
  | 'experience_pci-cog'
  | 'experience_cog_subj_risktaking'
  | 'experience_bart'
  | 'experience_ius'
  | 'experience_cog_subj_flexibility'
  | 'experience_cogstroop'
  | 'experience_lms'
  | 'experience_soc_subj_cooperation'
  | 'experience_ultimatum'
  | 'experience_pci-soc-ins'
  | 'experience_soc_subj_empathy'
  | 'experience_rme'
  | 'experience_cs-sf'
  | 'experience_pci-soc-emo'
  // Tingari customization
  | 'survey_mhc'
  | 'survey_ius'
  | 'test_oddball'
  | 'test_bart'
  | 'survey_bpnsfs'
  | 'survey_eqri'
  | 'survey_hexaco-pi-r'
  | 'survey_lsas'
  | 'survey_pswq'
  | 'survey_rse'
  | 'survey_sep-tic'
  | 'survey_stai-y'
  | 'survey_gse'

export type JourneyStepType =
  | 'misc'
  | 'report'
  | 'report_interaction'
  | 'survey'
  | 'game'
  | 'vr_game'
  | 'api_action'
  | 'trigger';
export type JourneyStepGroup =
  | 'experience'
  | 'impact';
export type ContactCategory =
  | "technical_issue"
  | "credit_or_plan_issue"
  | "request_analysis"

export type CreditMode =
  | "per_usage"
  | "subscription"

export interface GetPersonAlias {
  first_name: string;
  last_name: string;
  created_at: DateString;
  updated_at?: DateString;
}

export interface GetJourneyStepSchema {
  group: JourneyStepGroup;
  content: {
    name: JourneyStepName;
    type: JourneyStepType;
    version: Version;
  };
  dependencies: JourneyStepId[];
}

export interface GetJourneyStepProps {
  name: JourneyStepId;
  data: {
    meta_data: {
      started_at: DateString | null;
      ended_at: DateString | null;
      is_valid?: boolean;
      is_finished?: boolean;
      is_available: boolean;
    };
  };
}

export interface JourneySchema {
  version: Version;
  steps: Record<JourneyStepId, GetJourneyStepSchema>;
  steps_not_allowed_in_quetzal: JourneyStepId[];
  reports: Record<JourneyStepName, {
    step: JourneyStepId;
    version: Version;
    elements: JourneyStepId[];
  }>;
  conditional_steps: Record<JourneyStepId, Record<Lang, {
    name: JourneyStepName;
    type: JourneyStepType;
    version: Version;
  }>>;
  steps_relationship: Record<JourneyStepId, JourneyStepId>;
  deletion_guards: JourneyStepId[];
}

export interface GetRoleCoachee {
  person_role: "coachee";
  workspace_id: WorkspaceId;
  created_at: DateString;
  updated_at?: DateString;
  organization_pids: OrganizationId[];
  promotion_pids: PromotionId[];
  journeys?: GetPersonJourney.Result[];
  vr_pin: VrPin | null;
}

export interface GetRoleStaff {
  person_role: Exclude<PersonRole, "coachee">;
  workspace_id: WorkspaceId;
  created_at: DateString;
  updated_at?: DateString;
}

export namespace GetMe {
  export interface Result {
    public_id: PersonId;
    first_name: string;
    last_name: string;
    email: Email;
    lang?: Lang;
    is_verified: boolean;
    lang_was_picked: boolean;
    alias?: {
      first_name: string;
      last_name: string;
      created_at: DateString;
      updated_at?: DateString;
    };
    roles: (GetRoleCoachee | GetRoleStaff)[];
    onboarding_tag: OnboardingTag;
    onboarding_data: object | null; // Only used by the my-omind
  }
}
export namespace PatchMe {
  export interface Params {
    first_name?: string;
    last_name?: string;
    lang?: Lang;
    onboarding_data?: object | null; // Only used by the my-omind
  }

  export type Result = GetMe.Result;
}

export namespace PostWorkspaceMeeting {
  export interface Params {
    journeys_pid: JourneyId[];
    meeting_name: MeetingName;
    meeting_alias: null | string;
    meeting_coach?: {
      coach_pid: PersonId;
      link: Url;
    }[];
  }

  export type Result = string;
}

export namespace GetJourneyMeeting {
  export interface Coach {
    coach_meeting_profile_pid: string;
    coach_meeting_link: string;
    first_name: string;
    last_name: string;
    person_pid: string;
  }

  export interface Result {
    meeting_name: MeetingName;
    has_coach_selection: boolean;
    coach_selection: Coach[]
  }
}
export namespace DeleteJourneyMeeting {
  export type Result = string;
}

export namespace GetJourneyExtern {
  export interface Result {
    extern_public_id: PersonId;
  }
}
export namespace PutJourneyExtern {
  export interface Params {
    extern_public_id: PersonId;
  }

  export type Result = string;
}
export namespace DeleteJourneyExtern {
  export type Result = string;
}

export namespace PostLegalSignature {
  export interface Params {
    contract_id: number;
  }

  export type Result = string;
}

export namespace GetWorkspaceCoachee {
  export interface Result {
    public_id: JourneyId;
    email: string;
    lang?: Lang;
    is_verified: boolean;
    workspace_id: WorkspaceId;
    person_alias: GetPersonAlias;
    person_roles: (GetRoleCoachee | GetRoleStaff)[];
  }
}

export namespace GetWorkspaceCoachees {
  export interface Query {
    page: undefined | number;
    page_size: undefined | number;
    journey_name: JourneyName[];
    organization_pid: string[];
    promotion_pid: string[];
    search: string | undefined;
    sort_by: SortBy | undefined;
  }

  export type Result = {
    pagination: {
      page: number;
      per_page: number;
      total_items: number;
    };
    data: GetWorkspaceCoachee.Result[];
  };

  export type SortBy = 'name_asc' | 'name_desc' | 'creation_asc' | 'creation_desc';
}

export namespace GetWorkspaceCoach {
  export interface Result {
    public_id: JourneyId;
    person_alias: GetPersonAlias;
    person_roles: (GetRoleStaff)[];
  }
}

export namespace GetWorkspaceCoaches {
  export interface Query {
  }

  export type Result = GetWorkspaceCoach.Result[];
}

export namespace PostWorkspaceCoachees {
  export type CoacheesParam = {
    first_name: string;
    last_name: string;
    email: string;
    organization_pids?: OrganizationId[];
    promotion_pids?: PromotionId[];
  }[];

  export type JourneyParam = {
    name: "bootcamp_online";
    steps: Record<JourneyStepId, { dependencies: JourneyStepId[] }>;
    alias?: string;
  } | {
    name: "tingari";
    steps: Record<JourneyStepId, { dependencies: JourneyStepId[] }>;
  } | {
    name: Exclude<JourneyName, "bootcamp_online" | "tingari">;
  } | {
    name: 'feta';
    unlock_ascent?: boolean;
  };

  export interface Params {
    coachees: CoacheesParam;
    journey?: JourneyParam;
  }

  export type Result = GetWorkspaceCoachee.Result[];
}
export namespace PatchWorkspaceCoachee {
  export interface Params {
    first_name?: string;
    last_name?: string;
    email?: Email;
    organization_pids?: null | OrganizationId[];
    promotion_pids?: null | PromotionId[];
  }

  export interface Result {
    email?: Email;
    person_alias?: GetPersonAlias;
    person_roles?: (GetRoleCoachee | GetRoleStaff)[];
  }
}

export namespace GetWorkspaceExtern {
  export interface Result {
    public_id: PersonId;
    person_alias: GetPersonAlias,
    person_roles: (GetRoleCoachee | GetRoleStaff)[];
  }
}
export namespace GetWorkspaceExterns {
  export type Result = GetWorkspaceExtern.Result[]
}

export namespace GetWorkspaceStaff {
  export interface Result {
    public_id: PersonId;
    email: string;
    is_verified: boolean;
    person_alias: GetPersonAlias,
    person_roles: (GetRoleCoachee | GetRoleStaff)[];
  }
}
export namespace GetWorkspaceStaffs {
  export type Result = GetWorkspaceStaff.Result[]
}

export namespace PostWorkspaceStaff {
  export type Params = {
    first_name: string;
    last_name: string;
    email: string;
    roles: PersonRole[];
  };

  export type Result = GetWorkspaceStaff.Result;
}

export namespace PatchWorkspaceStaff {
  export interface Params {
    first_name?: string;
    last_name?: string;
    email?: Email;
    roles: PersonRole[];
  }

  export interface Result {
    email?: Email;
    person_alias?: {
      created_at: DateString;
      updated_at?: DateString;
      first_name: string;
      last_name: string;
    };
    person_roles?: Partial<Record<PersonRole, null | {
      person_id: PersonId;
      workspace_id: WorkspaceId;
      created_at: DateString;
      updated_at?: DateString;
      person_role: PersonRole;
      organization_pids?: OrganizationId[];
      promotion_pids?: PromotionId[];
    }>>;
  }
}

export namespace DeleteWorkspaceStaff {
  export type Result = string;
}

export namespace GetPersonJourney {
  export interface Result {
    public_id: JourneyId;
    // workspace_id: WorkspaceId;
    name: JourneyName;
    created_at: DateString;
    updated_at?: DateString;
    is_closed: boolean;
    journey_schema: JourneySchema;
    steps: GetJourneyStepProps[];
    extern_pid?: PersonId;
  }
}
export namespace GetPersonJourneys {
  export type Result = GetPersonJourney.Result[];
}
export namespace PostWorkspacePersonJourney {
  interface TingariParams {
    journey_name: "tingari";
    steps: Record<JourneyStepId, { dependencies: JourneyStepId[] }>;
  }

  interface BootcampOnlineParams {
    journey_name: "bootcamp_online";
    steps: Record<JourneyStepId, { dependencies: JourneyStepId[] }>;
    alias?: string;
  }

  interface StandardParams {
    journey_name: Exclude<JourneyName, "tingari" | "bootcamp_online">;
  }

  export type Params = TingariParams | BootcampOnlineParams | StandardParams;

  export type Result = GetPersonJourney.Result;
}
export namespace PostWorkspacePersonJourneys {
  export interface Params {
    journey_name: JourneyName;
    steps?: Record<JourneyStepId, { dependencies: JourneyStepId[]; }>;
    alias?: string;
    persons_pid: PersonId[];
    unlock_ascent?: boolean;
  }

  export type Result = GetPersonJourney.Result[];
}
export namespace DeleteWorkspacePersonRole {
  export type Result = string;
}

export namespace GetWorkspace {
  // Returns the workspace from the point of view of the current user.
  export interface Result {
    id: WorkspaceId;
    name: string;
    config: {
      allow_promotion: boolean;
    };
    allow_meeting_coach_selection: boolean;
    allow_automatic_email: boolean;
    roles: PersonRole[]; // user <-> workspace
  }

  export type ResultWithCredits = Result & {
    credit: Partial<Record<JourneyName, { unexpired_credit: number, assigned_credit: number, credit_expiration: any }>>;
    credit_mode: CreditMode;
  }
}
export namespace GetWorkspaces {
  export type Result = GetWorkspace.Result[];
}
export namespace GetWorkspaceCreditsOld {
  export type Result = Partial<Record<JourneyName, { total_credits: number, used_credits: number }>>;
}
export namespace GetWorkspaceCredits {
  export type Result = Partial<Record<JourneyName, { unexpired_credit: number, assigned_credit: number, credit_expiration: any }>>;
}

export namespace GetReportPDFUrl {
  export interface Query {
    report_variant: 'feta_launch_variant';
  }

  export interface Result {
    url: Url;
  }
}
export namespace GetJourneyStep {
  export interface Result {
    name: JourneyStepId;
    data: any; // TODO
  }
}
export namespace PutJourneyStep {
  export type Params = any; // TODO

  export type Result = GetJourneyStep.Result;
}
export namespace PatchJourneyStep {
  export interface Params {
    data: {
      meta_data: {
        is_available: boolean;
      };
    };
  }

  export type Result = GetJourneyStep.Result;
}

export namespace PatchJourneySchema {
  export interface Params {
    steps: JourneyStepName[];
  }

  export interface Result {
    journey_schema: JourneySchema;
  }
}
export namespace PatchJourney {
  export interface Params {
    is_closed: true;
  }

  export type Result = string;
}
export namespace DeleteJourney {
  export type Result = string;
}

export namespace PatchPersonPassword {
  export type Params = {
    password: string;
    old_password: string;
  } | {
    password: string;
    token: string;
  };

  export type Result = string;
}
export namespace VerifyAccount {
  export interface Params {
    url_token: string;
  }

  export type Result = string;
}
export namespace VerifyAccountAgain {
  export interface Params {
    url_token: string;
  }

  export type Result = string;
}
export namespace GetSessionStatus {
  export interface Result {
    status: 'is_alive';
  }
}
export namespace PostPasswordReset {
  export interface Params {
    email: Email;
  }

  export type Result = string;
}
export namespace SignUp {
  export interface Params {
    email: string;
    password: string;
    accept_cgu: boolean;
    accept_legal_mentions: boolean;
    lang: Lang;
  }

  export type Result = string;
}
export namespace SignIn {
  export interface Params {
    username: string;
    password: string;
  }

  export type Result = string;
}
export namespace SignOut {
  export type Result = string;
}

export namespace GetWorkspaceOrganization {
  export interface Result {
    pid: OrganizationId;
    workspace_id: WorkspaceId;
    created_at: DateString;
    updated_at?: DateString;
    organization_name: string;
  }
}
export namespace GetWorkspaceOrganizations {
  export type Result = GetWorkspaceOrganization.Result[];
}
export namespace PostWorkspaceOrganization {
  export interface Params {
    organization_name: string;
  }

  export type Result = GetWorkspaceOrganization.Result;
}

export namespace GetWorkspacePromotion {
  export interface Result {
    pid: PromotionId;
    workspace_id: WorkspaceId;
    created_at: DateString;
    updated_at?: DateString;
    promotion_name: string;
  }
}
export namespace GetWorkspacePromotions {
  export type Result = GetWorkspacePromotion.Result[];
}
export namespace PostWorkspacePromotion {
  export interface Params {
    promotion_name: string;
  }

  export type Result = GetWorkspacePromotion.Result;
}

export namespace GetJourneyExternId {

  export interface Result {
    extern_public_id: PersonId;
  }
}
export namespace PutJourneyExternId {
  export interface Params {
    extern_public_id: PersonId;
  }

  export type Result = string;
}
export namespace DeleteJourneyExternId {
  export type Result = string;
}
export namespace ContactUs {
  export interface Params {
    category: ContactCategory;
    message: string;
  }

  export type Result = string;
}

export namespace Template {
  export interface Params {
  }

  export interface Result {
  }
}

function toQueryString(query: any): string {
  const parts: string[] = [];

  if (query === null || query === undefined) return '';

  for (const key in query) {
    const value = query[key];

    if (value === undefined) continue;

    const values: any[] = Array.isArray(value) ? value : [value];

    for (const item of values)
      if (typeof item === "number" || typeof item === "string" || typeof item === "boolean")
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(item)}`);
  }

  return parts.length ? `?${parts.join('&')}` : '';
}


export interface HttpClient {
  post(url: string, body?: any): Promise<any>;

  get(url: string, config?: AxiosRequestConfig): Promise<any>;

  put(url: string, body?: any): Promise<any>;

  patch(url: string, body?: any): Promise<any>;

  delete(url: string): Promise<any>;
}

export class SDK {
  constructor(private api: HttpClient) {
  }

  // AUTHENTICATION
  patchPersonPassword(params: PatchPersonPassword.Params): Response<PatchPersonPassword.Result> {
    return this.api.patch(`/person/password`, params);
  }

  verifyAccount(params: VerifyAccount.Params): Response<VerifyAccount.Result> {
    return this.api.post(`/verify`, params);
  }

  verifyAccountAgain(params: VerifyAccountAgain.Params): Response<VerifyAccountAgain.Result> {
    return this.api.post(`/new-verification`, params);
  }

  getSessionStatus(): Response<GetSessionStatus.Result> {
    return this.api.get(`/session-status`);
  }

  postPasswordReset(params: PostPasswordReset.Params): Response<PostPasswordReset.Result> {
    return this.api.post(`/password-reset`, params);
  }

  signUp(params: SignUp.Params): Response<SignUp.Result> {
    return this.api.post(`/signup`, params);
  }

  signIn(params: SignIn.Params): Response<SignIn.Result> {
    return this.api.post(`/login`, params);
  }

  signOut(): Response<SignOut.Result> {
    return this.api.post(`/logout`);
  }

  // LEGAL
  postLegalSignature(params: PostLegalSignature.Params): Response<PostLegalSignature.Result> {
    return this.api.post(`/legal-signature`, params);
  }

  // ME
  getMe(): Response<GetMe.Result> {
    return this.api.get(`/me`);
  }

  patchMe(params: PatchMe.Params): Response<PatchMe.Result> {
    return this.api.patch(`/me`, params);
  }

  getWorkspaces(): Response<GetWorkspaces.Result> {
    return this.api.get(`/person/me/workspaces`);
  }

  getWorkspace(workspaceId: WorkspaceId): Response<GetWorkspace.ResultWithCredits> {
    return this.api.get(`/workspace/${workspaceId}`)
  }

  getWorkspaceCredits(workspaceId: WorkspaceId): Response<GetWorkspaceCredits.Result> {
    return this.api.get(`/workspace/${workspaceId}/credits`)
  }

  // PERSON
  getWorkspaceCoachees(workspaceId: WorkspaceId, query: GetWorkspaceCoachees.Query, config?: AxiosRequestConfig): Response<GetWorkspaceCoachees.Result> {
    return this.api.get(`/workspace/${workspaceId}/coachees${toQueryString(query)}`, config);
  }

  getWorkspaceCoaches(workspaceId: WorkspaceId, query: GetWorkspaceCoaches.Query, config?: AxiosRequestConfig): Response<GetWorkspaceCoaches.Result> {
    return this.api.get(`/workspace/${workspaceId}/coaches${toQueryString(query)}`, config);
  }

  postWorkspaceCoachees(workspaceId: WorkspaceId, params: PostWorkspaceCoachees.Params): Response<PostWorkspaceCoachees.Result> {
    return this.api.post(`/workspace/${workspaceId}/coachees`, params);
  }

  patchWorkspaceCoachee(workspaceId: WorkspaceId, personId: PersonId, params: PatchWorkspaceCoachee.Params): Response<PatchWorkspaceCoachee.Result> {
    return this.api.patch(`/workspace/${workspaceId}/person/${personId}/role/coachee`, params);
  }

  /* patchWorkspaceStaff(workspaceId: WorkspaceId, personId: PersonId, params: PatchWorkspaceStaff.Params): Response<PatchWorkspaceStaff.Result> {
    return this.api.patch(`/workspace/${workspaceId}/person/${personId}/role/staff`, params);
  } */

  postWorkspacePersonJourneys(workspaceId: WorkspaceId, params: PostWorkspacePersonJourneys.Params): Response<PostWorkspacePersonJourneys.Result> {
    return this.api.post(`/workspace/${workspaceId}/journeys`, params);
  }

  deleteWorkspacePersonRole(workspaceId: WorkspaceId, personId: PersonId, role: WorkspacePersonRoleCategory): Response<DeleteWorkspacePersonRole.Result> {
    return this.api.delete(`/workspace/${workspaceId}/person/${personId}/role/${role}`);
  }

  // EXTERNS
  getWorkspaceExterns(workspaceId: WorkspaceId): Response<GetWorkspaceExterns.Result> {
    return this.api.get(`/workspace/${workspaceId}/externs`);
  }

  // STAFF
  getWorkspaceStaff(workspaceId: WorkspaceId): Response<GetWorkspaceStaffs.Result> {
    return this.api.get(`/workspace/${workspaceId}/staff`);
  }

  postWorkspaceStaff(workspaceId: WorkspaceId, params: PostWorkspaceStaff.Params): Response<PostWorkspaceStaff.Result> {
    return this.api.post(`/workspace/${workspaceId}/staff`, params);
  }

  patchWorkspaceStaff(workspaceId: WorkspaceId, personId: PersonId, params: PatchWorkspaceStaff.Params): Response<PatchWorkspaceStaff.Result> {
    return this.api.patch(`/workspace/${workspaceId}/staff/${personId}`, params);
  }

  deleteWorkspaceStaff(workspaceId: WorkspaceId, personId: PersonId): Response<DeleteWorkspaceStaff.Result> {
    return this.api.delete(`/workspace/${workspaceId}/staff/${personId}`);
  }

  // JOURNEY
  patchJourney(journeyId: JourneyId, params: PatchJourney.Params): Response<PatchJourney.Result> {
    return this.api.patch(`journey/${journeyId}`, params);
  }

  deleteJourney(journeyId: JourneyId): Response<DeleteJourney.Result> {
    return this.api.delete(`journey/${journeyId}`);
  }

  // JOURNEY EXTERNS
  getJourneyExtern(journeyId: JourneyId): Response<GetJourneyExtern.Result> {
    return this.api.get(`/journey/${journeyId}/coach-extern`);
  }

  putJourneyExtern(journeyId: JourneyId, params: PutJourneyExtern.Params): Response<PutJourneyExtern.Result> {
    return this.api.put(`/journey/${journeyId}/coach-extern`, params);
  }

  deleteJourneyExtern(journeyId: JourneyId): Response<DeleteJourneyExtern.Result> {
    return this.api.delete(`/journey/${journeyId}/coach-extern`);
  }

  // MEETINGS
  postWorkspaceMeeting(workspaceId: WorkspaceId, params: PostWorkspaceMeeting.Params): Response<PostWorkspaceMeeting.Result> {
    return this.api.post(`/workspace/${workspaceId}/meeting`, params);
  }

  getJourneyMeeting(journeyId: JourneyId, meetingName: MeetingName): Response<GetJourneyMeeting.Result> {
    return this.api.get(`/journey/${journeyId}/meeting/${meetingName}`);
  }

  deleteJourneyMeeting(journeyId: JourneyId, meetingName: MeetingName): Response<DeleteJourneyMeeting.Result> {
    return this.api.delete(`/journey/${journeyId}/meeting/${meetingName}`);
  }

  // JOURNEY STEP
  getReportPDFUrl(journeyId: JourneyId, stepName: JourneyStepName, query?: GetReportPDFUrl.Query): Response<GetReportPDFUrl.Result> {
    return this.api.get(`/journey/${journeyId}/report/${stepName}/pdf-url${toQueryString(query)}`);
  }

  getJourneyStep(journeyId: JourneyId, stepId: JourneyStepId): Response<GetJourneyStep.Result> {
    return this.api.get(`/journey/${journeyId}/step/${stepId}`);
  }

  putJourneyStep(journeyId: JourneyId, stepId: JourneyStepId, params: PutJourneyStep.Params): Response<PutJourneyStep.Result> {
    return this.api.put(`/journey/${journeyId}/step/${stepId}`, params);
  }

  patchJourneyStep(journeyId: JourneyId, stepId: JourneyStepId, params: PatchJourneyStep.Params): Response<PatchJourneyStep.Result> {
    return this.api.patch(`/journey/${journeyId}/step/${stepId}`, params);
  }

  getWorkspaceOrganizations(workspaceId: WorkspaceId): Response<GetWorkspaceOrganizations.Result> {
    return this.api.get(`/workspace/${workspaceId}/organizations`);
  }

  postWorkspaceOrganization(workspaceId: WorkspaceId, data: PostWorkspaceOrganization.Params): Response<PostWorkspaceOrganization.Result> {
    return this.api.post(`/workspace/${workspaceId}/organization`, data);
  }

  getWorkspacePromotions(workspaceId: WorkspaceId): Response<GetWorkspacePromotions.Result> {
    return this.api.get(`/workspace/${workspaceId}/promotions`);
  }

  postWorkspacePromotion(workspaceId: WorkspaceId, data: PostWorkspacePromotion.Params): Response<PostWorkspacePromotion.Result> {
    return this.api.post(`/workspace/${workspaceId}/promotion`, data);
  }

  contactUs(params: ContactUs.Params): Response<ContactUs.Result> {
    return this.api.post(`/email-support`, params);
  }
}

export const sdk = new SDK(
  axios.create({
    withCredentials: true,
    baseURL: import.meta.env.VITE_API_URL,
    headers: new axios.AxiosHeaders({
      "Omind-Application": "hub.omind"
    })
  })
);

export default sdk
